import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Pagination } from "semantic-ui-react";
import { getCallRecordingTranscriptions } from "../../api/transcriptions";
import { BUSY_CALL_STATUSES } from "../../hooks/useCalls";
import { DataTable, EmptyState, ErrorState, LoadingState } from "../ui";
import CallTranscriptionRow, { COLUMNS } from "./CallTranscriptionRow";

const CallsTable = ({ children }) => (
  <DataTable
    scroll
    rowGroups
    unstackable
    columns={COLUMNS.map((c) => ({ key: c, label: c }))}
  >
    {children}
  </DataTable>
);

// Connect has no Algolia search, so this is the paged API list only
const CallTranscriptions = ({ searchParams }) => {
  // A page belongs to the search it was picked in, so a new search is on
  // page 1 from its first render (no fetch for the old page number).
  const [pageState, setPageState] = useState({ page: 1, searchParams });
  const page = pageState.searchParams === searchParams ? pageState.page : 1;

  const queryClient = useQueryClient();

  const transcriptionsQuery = useQuery(
    ["call-transcriptions", page, searchParams],
    () => {
      return getCallRecordingTranscriptions("", {
        page: page,
        hydrate: "0",
        ...searchParams,
      });
    },
    {
      keepPreviousData: true,
      staleTime: 60000,
      refetchIntervalInBackground: true,
      refetchInterval: (data) => {
        let interval = false;
        data?.data.data.forEach((i) => {
          if (BUSY_CALL_STATUSES.includes(i?.status.value)) {
            queryClient.invalidateQueries({ queryKey: ["call-recordings"] });
            interval = 30 * 1000;
          }
        });
        return interval;
      },
    },
  );

  const handlePaginationChange = (e, { activePage }) => {
    setPageState({ page: activePage, searchParams });
  };

  if (transcriptionsQuery.isLoading) return <LoadingState />;

  const filtered = Object.values(searchParams ?? {}).some(Boolean);

  const list = () => {
    if (transcriptionsQuery.isError) {
      return (
        <ErrorState
          onRetry={() => transcriptionsQuery.refetch()}
          retryLabel="Try again"
        >
          Couldn't load your calls.
        </ErrorState>
      );
    }
    const calls = transcriptionsQuery.data.data.data;
    if (calls.length === 0) {
      return (
        <EmptyState inline>
          {filtered ? "No calls match that search." : "No processed calls yet."}
        </EmptyState>
      );
    }
    return (
      <>
        <CallsTable>
          {calls.map((c) => (
            <CallTranscriptionRow key={c.sqid} call={c} />
          ))}
        </CallsTable>
        <div style={{ width: "100%", textAlign: "center" }}>
          <Pagination
            inverted
            activePage={page}
            totalPages={transcriptionsQuery.data.data.meta.last_page}
            onPageChange={handlePaginationChange}
          />
        </div>
      </>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {list()}
    </div>
  );
};

export default CallTranscriptions;
